/**
 * Lot 335 · Bloc 1 — Bandeau overlay "Mode aperçu audience".
 *
 * Affiché en position fixed en haut de page quand un admin navigue
 * avec `?_preview=public|investor|sponsor`. Rappelle clairement le mode
 * simulé et propose un bouton pour sortir.
 *
 * Design sobre, non intrusif, cohérent avec la direction éditoriale
 * "bronze sur charcoal" déjà posée dans les Lots 333-334.
 */
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Globe, User, Briefcase, Eye, X } from '@phosphor-icons/react';

const AUDIENCE_META = {
  public:   { label: 'Visiteur anonyme',    Icon: Globe },
  investor: { label: 'Investisseur connecté', Icon: User },
  sponsor:  { label: 'Sponsor connecté',    Icon: Briefcase },
};

export default function AudiencePreviewBanner({ audience }) {
  const location = useLocation();
  const navigate = useNavigate();

  const cleanUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.delete('_preview');
    params.delete('_pid');
    params.delete('_v');
    const qs = params.toString();
    return location.pathname + (qs ? `?${qs}` : '');
  }, [location.search, location.pathname]);

  if (!audience || !AUDIENCE_META[audience]) return null;
  const meta = AUDIENCE_META[audience];
  const Icon = meta.Icon;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] bg-charcoal text-ivory border-b border-bronze shadow-elevated"
      data-testid="audience-preview-banner"
      role="status"
      aria-live="polite"
    >
      <div className="container-premium py-2 px-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-bronze text-charcoal text-[9px] uppercase tracking-[0.2em] font-semibold flex-shrink-0">
            <Eye size={10} weight="fill" /> Aperçu
          </span>
          <p className="text-sm truncate">
            <span className="text-ivory/60">Vue simulée :</span>{' '}
            <strong className="inline-flex items-center gap-1.5">
              <Icon size={13} weight="duotone" className="text-bronze-light" />
              {meta.label}
            </strong>
          </p>
        </div>
        <button
          onClick={() => navigate(cleanUrl, { replace: true })}
          className="text-[10px] uppercase tracking-[0.18em] text-ivory/70 hover:text-bronze-light inline-flex items-center gap-1.5 flex-shrink-0"
          data-testid="audience-preview-exit"
          title="Quitter le mode aperçu"
        >
          Quitter <X size={11} weight="bold" />
        </button>
      </div>
    </div>
  );
}
